// ---------------------------------------------------------------------------
// live.ts -- Bounded Live client (drive a server-authoritative room).
//
// A Bounded "live" room is a native module (init/tick/views) loaded into an
// isolated Durable Object facet inside the per-room DO. The worker runs tick
// ~30Hz server-authoritatively and fans per-player views out on an ephemeral
// collection. This file is the consumer surface around that runtime:
//
//   bounded.live.intent(roomPath, intent)  -> POST {realtime}/live/intent
//   bounded.live.status(roomPath)          -> GET  {realtime}/live/status
//
// It mirrors `functions.invoke` exactly: it attaches the caller's session token
// AUTOMATICALLY (the same token the SDK uses for data reads/writes), and throws
// a typed error on any non-2xx / transport / timeout.
//
// The runtime serves any server-authoritative realtime room (Figma clones,
// editors, dashboards, multiplayer games, ...). Games remain a valid use case;
// the primitive itself is just "live".
//
// Addressing: clients address rooms BY PATH (the `path` field in the body). The
// worker derives the roomId from `body.path` and sets X-Room-Id internally;
// clients NEVER set X-Room-Id. The caller's address is taken server-side from
// the Authorization header (auth.userId/userAddress) — it is NOT sent in the
// body.
//
// Subscribing to your view: a per-player view doc lives at
// `<roomPath>/view/<myUserId>` (the policy declares
// `rooms/$roomId/view/$userId` ephemeral with `read: $userId == @user.id`).
// View paths key only by @user.id; wallet-address aliases are intentionally not
// accepted by this helper.
// ---------------------------------------------------------------------------
import { getConfig } from './config';
import { createAuthHeader, getUserInfo } from '../utils/utils';
import { subscribeV2, wsIntent, wsIntentReliable } from './subscription-v2';
import { deriveDecline, dispatchDeclineEvent } from './decline';
export class LiveIntentError extends Error {
    constructor(message, statusCode, details) {
        super(message);
        this.statusCode = statusCode;
        this.details = details;
        this.name = 'LiveIntentError';
    }
}
/**
 * Derive the realtime worker's HTTP base from the configured WS API URL.
 * The realtime worker serves both WS and HTTP on the same host, so we reuse
 * `config.wsApiUrl` (wss:// -> https://, ws:// -> http://). The data API
 * (`config.apiUrl`) is a DIFFERENT host and must NOT be used for /live/*.
 */
function realtimeHttpBase(wsApiUrl) {
    const url = new URL(wsApiUrl);
    if (url.protocol === 'wss:')
        url.protocol = 'https:';
    else if (url.protocol === 'ws:')
        url.protocol = 'http:';
    // Strip trailing slash from the resulting origin+path.
    return url.toString().replace(/\/$/, '');
}
function withoutAuthorization(headers) {
    if (!headers)
        return undefined;
    const clean = {};
    for (const [key, value] of Object.entries(headers)) {
        if (key.toLowerCase() === 'authorization')
            continue;
        clean[key] = value;
    }
    return Object.keys(clean).length > 0 ? clean : undefined;
}
function hasAuthorization(headers) {
    if (!headers)
        return false;
    return Object.entries(headers).some(([key, value]) => key.toLowerCase() === 'authorization' &&
        typeof value === 'string' &&
        /^Bearer\s+\S+$/i.test(value.trim()));
}
async function liveAuthHeader(configIsServer, overrides) {
    if (overrides === null || overrides === void 0 ? void 0 : overrides._getAuthHeaders) {
        return overrides._getAuthHeaders();
    }
    return createAuthHeader(configIsServer);
}
/**
 * Send a player intent to a running live room. Returns `{ ok: true }`.
 *
 *   await bounded.live.intent('rooms/abc', { type: 'move', dir: 'up' });
 *
 * `roomPath` is the document path of the room (e.g. `rooms/abc`). The worker
 * derives the room from this path and reads `intent` as the opaque per-tick
 * input. The caller's address is taken from the attached session token.
 *
 * Throws LiveIntentError on 401 (not logged in / bad token), 404 (room has no
 * live module), 503 (live runtime/module unavailable), or any non-2xx /
 * transport error / timeout.
 */
export async function intent(roomPath, intent, opts = {}) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    if (!roomPath || typeof roomPath !== 'string') {
        throw new LiveIntentError('A room path is required');
    }
    // A server WalletClient passes its immutable config snapshot; the default path
    // reads the mutable process-global init() config. This keeps a wallet client's
    // live intents on its original app/realtime endpoint after a later global
    // init() (CTA-05).
    const config = (_b = (_a = opts._overrides) === null || _a === void 0 ? void 0 : _a._config) !== null && _b !== void 0 ? _b : await getConfig();
    const initialAuthHeader = await liveAuthHeader(config.isServer, opts._overrides);
    if (!hasAuthorization(initialAuthHeader)) {
        if (opts.fireAndForget)
            return { ok: true };
        throw new LiveIntentError('Authentication required to send a live intent. Call init() and sign in before sending live room intents.', 401);
    }
    // Ride the already-open per-room socket instead of a fresh HTTP POST per
    // intent. That is what makes high-frequency input (~20-30Hz) cheap — one
    // persistent connection, no per-intent TCP/TLS or connection-pool contention
    // (which was head-of-line-blocking the view stream). The worker is the room
    // authority (the connection is routed to the room DO), so we pass no
    // destination.
    //
    //   - fireAndForget: send and return immediately (lowest latency for inputs).
    //   - default: send WITH a requestId and AWAIT the ack, so a policy/auth deny
    //     (401/403/404/503) REJECTS — important intents (join/ready) must not
    //     silently "succeed".
    //
    // Either way we fall back to HTTP below only when no live socket exists yet
    // (e.g. the first join before subscribeView's WS connects) — HTTP also throws
    // on a non-2xx, so errors are surfaced there too.
    const normalizedRoomPath = roomPath.replace(/\/$/, '');
    const hasAuthOverride = !!((_c = opts._overrides) === null || _c === void 0 ? void 0 : _c._getAuthHeaders);
    if (!hasAuthOverride) {
        if (opts.fireAndForget) {
            if (await wsIntent(config.appId, normalizedRoomPath, intent, config.isServer))
                return { ok: true };
        }
        else {
            const ack = await wsIntentReliable(config.appId, normalizedRoomPath, intent, config.isServer);
            if (ack)
                return ack;
        }
    }
    const base = realtimeHttpBase(config.wsApiUrl);
    const extraHeaders = withoutAuthorization(opts.headers);
    const overrideHeaders = withoutAuthorization((_d = opts._overrides) === null || _d === void 0 ? void 0 : _d.headers);
    const buildHeaders = async () => {
        const authHeader = await liveAuthHeader(config.isServer, opts._overrides);
        if (!hasAuthorization(authHeader)) {
            throw new LiveIntentError('Authentication required to send a live intent. Call init() and sign in before sending live room intents.', 401);
        }
        return Object.assign(Object.assign(Object.assign({ 'Content-Type': 'application/json', 'X-App-Id': config.appId }, (overrideHeaders !== null && overrideHeaders !== void 0 ? overrideHeaders : {})), (extraHeaders !== null && extraHeaders !== void 0 ? extraHeaders : {})), (authHeader !== null && authHeader !== void 0 ? authHeader : {}));
    };
    const controller = new AbortController();
    const timeoutMs = (_e = opts.timeoutMs) !== null && _e !== void 0 ? _e : 60000;
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    let res;
    try {
        const send = async () => fetch(`${base}/live/intent`, {
            method: 'POST',
            headers: await buildHeaders(),
            body: JSON.stringify({ path: normalizedRoomPath, intent }),
            signal: controller.signal,
        });
        res = await send();
        if (res.status === 401 && ((_f = opts._overrides) === null || _f === void 0 ? void 0 : _f._clearAuth)) {
            await opts._overrides._clearAuth();
            res = await send();
        }
    }
    catch (err) {
        clearTimeout(timer);
        if (err instanceof LiveIntentError)
            throw err;
        if ((err === null || err === void 0 ? void 0 : err.name) === 'AbortError') {
            throw new LiveIntentError(`Live intent to "${roomPath}" timed out after ${timeoutMs}ms`);
        }
        throw new LiveIntentError(`Failed to reach the realtime worker: ${(_g = err === null || err === void 0 ? void 0 : err.message) !== null && _g !== void 0 ? _g : String(err)}`);
    }
    clearTimeout(timer);
    let body = null;
    const text = await res.text();
    if (text) {
        try {
            body = JSON.parse(text);
        }
        catch (_k) {
            body = { raw: text };
        }
    }
    if (!res.ok) {
        const message = (_j = (_h = body === null || body === void 0 ? void 0 : body.error) !== null && _h !== void 0 ? _h : body === null || body === void 0 ? void 0 : body.message) !== null && _j !== void 0 ? _j : `Live intent failed with HTTP ${res.status}`;
        const liveError = new LiveIntentError(message, res.status, body);
        // A policy/invariant denial of a live intent carries the same structured
        // decline as the data-write path. Mirror `.decline` onto the LiveIntentError
        // (its name/contract is unchanged) and notify the browser widget.
        const decline = deriveDecline(body, res.status);
        if (decline) {
            liveError.decline = decline;
            liveError.isBoundedDecline = true;
            dispatchDeclineEvent(decline, config.appId);
        }
        throw liveError;
    }
    return (body !== null && body !== void 0 ? body : { ok: true });
}
/**
 * Fetch the runtime status for a live room.
 *
 *   const s = await bounded.live.status('rooms/abc');
 *   console.log(s.running, s.stopReason, s.generation, s.etag);
 *
 * This is diagnostic/ops surface. It reports whether the room facet exists,
 * whether it is currently ticking, why it last stopped, which module etag is
 * loaded, and the current generation used after terminal restarts.
 *
 * Auth: status attaches the caller's session token WHEN ONE EXISTS (mirroring
 * `intent`), but does NOT require it. The server gates the DETAILED body on the
 * room's read rule — an authenticated caller who can read the room gets full
 * telemetry; an anonymous / unauthorized caller gets only the slim liveness
 * shape `{available, started, module}` (#102). The caller's own `opts.headers`
 * may never smuggle in Authorization: only the derived session token sets it.
 */
export async function status(roomPath, opts = {}) {
    var _a, _b, _c, _d, _e, _f, _g;
    if (!roomPath || typeof roomPath !== 'string') {
        throw new LiveIntentError('A room path is required');
    }
    const normalizedRoomPath = roomPath.replace(/\/$/, '');
    // Honor a wallet client's immutable config snapshot (CTA-05); otherwise read
    // the mutable process-global init() config.
    const config = (_b = (_a = opts._overrides) === null || _a === void 0 ? void 0 : _a._config) !== null && _b !== void 0 ? _b : await getConfig();
    const base = realtimeHttpBase(config.wsApiUrl);
    const authHeader = await liveAuthHeader(config.isServer, opts._overrides);
    const extraHeaders = withoutAuthorization(opts.headers);
    const overrideHeaders = withoutAuthorization((_c = opts._overrides) === null || _c === void 0 ? void 0 : _c.headers);
    const headers = Object.assign(Object.assign(Object.assign({ 'X-App-Id': config.appId }, (overrideHeaders !== null && overrideHeaders !== void 0 ? overrideHeaders : {})), (extraHeaders !== null && extraHeaders !== void 0 ? extraHeaders : {})), (hasAuthorization(authHeader) ? authHeader : {}));
    const controller = new AbortController();
    const timeoutMs = (_d = opts.timeoutMs) !== null && _d !== void 0 ? _d : 15000;
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    let res;
    try {
        res = await fetch(`${base}/live/status?path=${encodeURIComponent(normalizedRoomPath)}`, {
            method: 'GET',
            headers,
            signal: controller.signal,
        });
    }
    catch (err) {
        clearTimeout(timer);
        if ((err === null || err === void 0 ? void 0 : err.name) === 'AbortError') {
            throw new LiveIntentError(`Live status for "${normalizedRoomPath}" timed out after ${timeoutMs}ms`);
        }
        throw new LiveIntentError(`Failed to reach the realtime worker: ${(_e = err === null || err === void 0 ? void 0 : err.message) !== null && _e !== void 0 ? _e : String(err)}`);
    }
    clearTimeout(timer);
    let body = null;
    const text = await res.text();
    if (text) {
        try {
            body = JSON.parse(text);
        }
        catch (_h) {
            body = { raw: text };
        }
    }
    if (!res.ok) {
        const message = (_g = (_f = body === null || body === void 0 ? void 0 : body.error) !== null && _f !== void 0 ? _f : body === null || body === void 0 ? void 0 : body.message) !== null && _g !== void 0 ? _g : `Live status failed with HTTP ${res.status}`;
        throw new LiveIntentError(message, res.status, body);
    }
    return body;
}
/**
 * Subscribe to YOUR per-player view of a room. Thin sugar over:
 *
 *   subscribe('<roomPath>/view/<myUserId>', { onData, onError })
 *
 * The view id defaults to the logged-in user's @user.id (from the session token
 * claims); pass `opts.userId` to override. Returns the unsubscribe function
 * (a Promise<() => Promise<void>>, same as `subscribe`).
 *
 * Server/agent consumers: use `WalletClient.live.subscribeView` (it supplies
 * the wallet session + view id); browser apps just call this directly.
 */
export async function subscribeView(roomPath, opts) {
    if (!roomPath || typeof roomPath !== 'string') {
        throw new LiveIntentError('A room path is required');
    }
    if (!opts || typeof opts.onData !== 'function') {
        throw new LiveIntentError('subscribeView requires an onData callback');
    }
    let viewUserId = opts.userId;
    if (!viewUserId) {
        const config = await getConfig();
        const info = await getUserInfo(config.isServer);
        // getUserInfo returns the RAW idToken payload. The universal live view key
        // is @user.id (`custom:userId`); wallet-address keyed compatibility aliases
        // are intentionally not accepted.
        viewUserId = info === null || info === void 0 ? void 0 : info['custom:userId'];
    }
    if (!viewUserId || typeof viewUserId !== 'string') {
        throw new LiveIntentError('Could not resolve a player view id for subscribeView; pass opts.userId or log in first');
    }
    const normalizedRoomPath = roomPath.replace(/\/$/, '');
    const viewPath = `${normalizedRoomPath}/view/${viewUserId}`;
    // This is a LIVE/room subscription by construction (the caller hands us the
    // room path), so we route its connection to the per-room DO where the live
    // view fan-out runs. App code passes NO routing — the room path is the helper's
    // own argument, and the worker stays the authority on the destination DO (it
    // derives the room from the `?path=` the connection carries). Without this the
    // subscription lands on the app-level project DO and never sees the room's
    // ephemeral view writes.
    return subscribeV2(viewPath, { onData: opts.onData, onError: opts.onError, _overrides: opts._overrides }, normalizedRoomPath);
}
/** The `bounded.live` namespace surface. */
export const live = { intent, status, subscribeView };
